/**
 * Display layout for World-DB import lists: custom order + hidden names.
 * Hidden lists stay selectable; they are only tucked under a collapsed section.
 */

export const EMPTY_LAYOUT = { hidden: [], order: [] };

export const sortByImportedDate = (a, b) => {
  const dateA = a.last_imported ? new Date(a.last_imported).getTime() : 0;
  const dateB = b.last_imported ? new Date(b.last_imported).getTime() : 0;
  return dateB - dateA;
};

export const sanitizeLayout = (raw) => {
  if (!raw || typeof raw !== 'object') return { hidden: [], order: [] };
  const hidden = Array.isArray(raw.hidden)
    ? raw.hidden.filter((n) => typeof n === 'string' && n.trim())
    : [];
  const order = Array.isArray(raw.order)
    ? raw.order.filter((n) => typeof n === 'string' && n.trim())
    : [];
  return {
    hidden: [...new Set(hidden)],
    order: [...new Set(order)],
  };
};

export const normalizeImportItems = (importOptions = []) =>
  (importOptions || [])
    .map((opt) => (typeof opt === 'string' ? { name: opt } : opt))
    .filter((item) => item && typeof item.name === 'string' && item.name);

/**
 * Names not yet in `order` (new imports) are prepended, newest first.
 * Stale names that no longer exist are dropped.
 */
export const resolveOrder = (items, order) => {
  const nameSet = new Set(items.map((i) => i.name));
  const known = (order || []).filter((n) => nameSet.has(n));
  const knownSet = new Set(known);
  const newcomers = items
    .filter((i) => !knownSet.has(i.name))
    .sort(sortByImportedDate)
    .map((i) => i.name);
  return [...newcomers, ...known];
};

export const applyListLayout = (items, layout) => {
  const sanitized = sanitizeLayout(layout);
  const hiddenSet = new Set(sanitized.hidden);
  const order = resolveOrder(items, sanitized.order);
  const byName = new Map(items.map((i) => [i.name, i]));
  const orderedItems = order.map((n) => byName.get(n)).filter(Boolean);
  return {
    visible: orderedItems.filter((i) => !hiddenSet.has(i.name)),
    hidden: orderedItems.filter((i) => hiddenSet.has(i.name)),
    order,
  };
};

export const moveList = (layout, items, name, direction) => {
  const sanitized = sanitizeLayout(layout);
  const { visible, hidden, order } = applyListLayout(items, sanitized);
  const hiddenSet = new Set(sanitized.hidden);
  const isHidden = hiddenSet.has(name);
  const group = (isHidden ? hidden : visible).map((i) => i.name);
  const idx = group.indexOf(name);
  if (idx < 0) return { ...sanitized, order };
  const next = idx + (direction === 'up' ? -1 : 1);
  if (next < 0 || next >= group.length) return { ...sanitized, order };
  const swapped = [...group];
  [swapped[idx], swapped[next]] = [swapped[next], swapped[idx]];
  let i = 0;
  return {
    hidden: sanitized.hidden.filter((n) => order.includes(n)),
    order: order.map((n) => (hiddenSet.has(n) === isHidden ? swapped[i++] : n)),
  };
};

export const setListHidden = (layout, items, name, hide) => {
  const sanitized = sanitizeLayout(layout);
  let order = resolveOrder(items, sanitized.order);
  if (!order.includes(name)) return { ...sanitized, order };
  const nextHidden = new Set(sanitized.hidden);
  if (hide) {
    nextHidden.add(name);
    order = [...order.filter((n) => n !== name), name];
  } else {
    nextHidden.delete(name);
  }
  return {
    order,
    hidden: order.filter((n) => nextHidden.has(n)),
  };
};

export const canMoveList = (layout, items, name, direction) => {
  const { visible, hidden } = applyListLayout(items, layout);
  const hiddenSet = new Set(sanitizeLayout(layout).hidden);
  const group = hiddenSet.has(name) ? hidden : visible;
  const idx = group.findIndex((i) => i.name === name);
  if (idx < 0) return false;
  return direction === 'up' ? idx > 0 : idx < group.length - 1;
};
